import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Button, CloseButton, Dialog, Icon, Input, Portal, Text, VStack } from '@chakra-ui/react'
import { RequiredFieldMark } from '@infraloop/common'
import { useTranslation } from '@infraloop/i18n'
import { Project, ProjectRequestBody } from '@infraloop/services'
import { LuFolderPlus, LuPencil } from 'react-icons/lu'

import useProjects from '../../hooks/useProjects'

const ProjectFormModal = ({
  isOpen,
  onClose,
  project,
}: {
  /** Whether the modal is open. */
  isOpen: boolean
  /** Called when the modal requests to close. */
  onClose: () => void
  /** Project to edit; omit to create a new one. */
  project?: Project
}) => {
  // Hooks
  const { t } = useTranslation()
  const { ownedProjects, createProject, isCreatingProject, updateProject, isUpdatingProject } = useProjects()

  // States
  const [projectName, setProjectName] = useState('')
  const [workType, setWorkType] = useState('')
  const [projectType, setProjectType] = useState('')

  // Derived state
  const isEdit = Boolean(project)
  const isSaving = isCreatingProject || isUpdatingProject
  const isNameDuplicate = useMemo(() => {
    const name = projectName.trim()
    if (!name) return false
    return ownedProjects.some((p) => p.project_name === name && p.project_id !== project?.project_id)
  }, [ownedProjects, projectName, project])

  /**
   * Reset the form whenever the modal (re)opens — prefilled from the project when editing.
   */
  useEffect(() => {
    if (!isOpen) return
    setProjectName(project?.project_name ?? '')
    setWorkType(project?.default_construction_properties?.construction_type ?? '')
    setProjectType(project?.default_construction_properties?.construction_type_detailed ?? '')
  }, [isOpen, project])

  /**
   * Create or update the project, then close. On failure the mutation hook has already
   * toasted the error, so the modal stays open for a retry.
   */
  const handleSubmit = useCallback(async () => {
    const body: ProjectRequestBody = {
      project_name: projectName.trim(),
      default_construction_properties: {
        ...project?.default_construction_properties,
        construction_type: workType.trim(),
        construction_type_detailed: projectType.trim(),
      } as Project['default_construction_properties'],
    }

    try {
      if (project) {
        await updateProject({ projectId: project.project_id, project: body })
      } else {
        await createProject({ project: body })
      }
      onClose()
    } catch {
      // Error feedback is handled by the mutation's onError toast.
    }
  }, [project, projectName, workType, projectType, createProject, updateProject, onClose])

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      size="md"
      placement="center"
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="16px" maxW="520px">
            <Dialog.Header borderBottom="1px solid" borderColor="gray.100" gap="12px" alignItems="center">
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="38px"
                h="38px"
                borderRadius="10px"
                bg="primary.50"
                color="primary.600"
                flexShrink={0}
              >
                <Icon as={isEdit ? LuPencil : LuFolderPlus} boxSize="19px" />
              </Box>
              <Dialog.Title flex="1" fontSize="19px" fontWeight="bold" color="gray.800">
                {isEdit ? t('hatsuly.modal.project.edit.title') : t('hatsuly.modal.project.create.title')}
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py="24px">
              <VStack align="stretch" gap="22px">
                <Box>
                  <Text as="label" display="block" fontSize="13px" fontWeight="semibold" color="gray.700" mb="7px">
                    {t('hatsuly.modal.project.create.nameLabel')} <RequiredFieldMark />
                  </Text>
                  <Input
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    borderRadius="9px"
                    bg="white"
                    data-testid="project-form-name"
                  />
                  {isNameDuplicate && (
                    <Text fontSize="12px" color="red.600" mt="6px">
                      {t('hatsuly.modal.project.duplicateError')}
                    </Text>
                  )}
                </Box>

                {/* Work-type / category seed per-area defaults. */}
                <Box>
                  <Text as="label" display="block" fontSize="13px" fontWeight="semibold" color="gray.700">
                    {t('hatsuly.modal.project.create.workTypeLabel')}
                  </Text>
                  <Text fontSize="12px" color="gray.400" mt="3px" mb="8px" lineHeight="1.55">
                    {t('hatsuly.modal.project.create.fieldHint')}
                  </Text>
                  <Input
                    value={workType}
                    onChange={(e) => setWorkType(e.target.value)}
                    borderRadius="8px"
                    bg="white"
                    data-testid="project-form-work-type"
                  />
                </Box>

                <Box>
                  <Text as="label" display="block" fontSize="13px" fontWeight="semibold" color="gray.700">
                    {t('hatsuly.modal.project.create.typeLabel')}
                  </Text>
                  <Text fontSize="12px" color="gray.400" mt="3px" mb="8px" lineHeight="1.55">
                    {t('hatsuly.modal.project.create.fieldHint')}
                  </Text>
                  <Input
                    value={projectType}
                    onChange={(e) => setProjectType(e.target.value)}
                    borderRadius="8px"
                    bg="white"
                    data-testid="project-form-type"
                  />
                </Box>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer borderTop="1px solid" borderColor="gray.100" bg="#FBFCFE" gap="10px">
              <Button variant="outline" onClick={onClose}>
                {t('ui.cancel')}
              </Button>
              <Button
                colorPalette="blue"
                disabled={!projectName.trim() || isNameDuplicate}
                loading={isSaving}
                onClick={handleSubmit}
                data-testid="project-form-submit"
              >
                {isEdit ? t('ui.save') : t('ui.create')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default ProjectFormModal
